/**
 * Maps every SVG data-zone label to the Part categories it contains,
 * and whether the zone can hold more than one part (multiPart).
 *
 * multiPart=true  → clicking a populated zone opens AddPartPanel (add more)
 * multiPart=false → clicking a populated zone opens ZoneManagePanel (manage)
 */
export const ZONE_CATEGORY_MAP: Record<string, { categories: string[]; multiPart: boolean }> = {
  'Muzzle Device': { categories: ['Muzzle Device'], multiPart: false },
  Barrel: { categories: ['Barrel'], multiPart: false },
  'Handguard / Rail': { categories: ['Handguard / Rail'], multiPart: false },
  'Handguard Upper': { categories: ['Handguard Upper'], multiPart: false },
  'Handguard Lower': { categories: ['Handguard Lower'], multiPart: false },
  'Gas Tube / Piston': { categories: ['Gas Tube / Piston'], multiPart: false },
  'Dust Cover': { categories: ['Dust Cover'], multiPart: false },
  'Bolt Carrier Group': { categories: ['Bolt Carrier Group'], multiPart: false },
  'Charging Handle': { categories: ['Charging Handle'], multiPart: false },
  'Upper Receiver': { categories: ['Upper Receiver'], multiPart: false },
  Optic: { categories: ['Optic'], multiPart: true }, // primary + magnifier
  'Light / Laser': { categories: ['Light / Laser'], multiPart: true }, // multiple lights/IR
  'Trigger Group': { categories: ['Trigger Group'], multiPart: false },
  'Lower Receiver': { categories: ['Lower Receiver'], multiPart: false },
  'Pistol Grip': { categories: ['Pistol Grip'], multiPart: false },
  'Stock / Brace': { categories: ['Stock / Brace'], multiPart: false },
  'Sling / Mount': { categories: ['Sling / Mount'], multiPart: true }, // multiple mount points
  'Side Rail Mount': { categories: ['Side Rail Mount'], multiPart: false },
  Magazine: { categories: ['Magazine', 'Magazine762', 'Magazine545', 'MagazineDrum'], multiPart: false },
  'Bayonet Lug': { categories: ['Bayonet Lug'], multiPart: false },
  Other: { categories: ['Other'], multiPart: true } // catch-all
}

const AR15_ZONE_ORDER = [
  'Muzzle Device', 'Barrel', 'Handguard / Rail', 'Bolt Carrier Group',
  'Charging Handle', 'Upper Receiver', 'Optic', 'Light / Laser',
  'Trigger Group', 'Lower Receiver', 'Pistol Grip', 'Stock / Brace',
  'Sling / Mount', 'Other'
]

const AK_ZONE_ORDER = [
  'Muzzle Device', 'Barrel', 'Handguard Upper', 'Handguard Lower',
  'Gas Tube / Piston', 'Dust Cover', 'Side Rail Mount', 'Optic',
  'Trigger Group', 'Magazine', 'Pistol Grip', 'Stock / Brace',
  'Bayonet Lug', 'Sling / Mount', 'Other'
]

/**
 * Maps platform names to their display zone order.
 * Platforms not listed default to AR-15 order.
 */
export const PLATFORM_ZONE_ORDER: Record<string, string[]> = {
  ar15: AR15_ZONE_ORDER,
  ak: AK_ZONE_ORDER,
  ak47: AK_ZONE_ORDER,
  ak74: AK_ZONE_ORDER,
  akm: AK_ZONE_ORDER,
  rpk: AK_ZONE_ORDER,
  pcc: AR15_ZONE_ORDER,
  dmr: AR15_ZONE_ORDER,
  'bolt-action': AR15_ZONE_ORDER,
  pistol: AR15_ZONE_ORDER,
  shotgun: AR15_ZONE_ORDER,
  other: AR15_ZONE_ORDER
}

/**
 * Returns the zone order for a given platform.
 * Lowercases the platform string before lookup.
 * Falls back to AR-15 order for unknown platforms.
 */
export function getPlatformZoneOrder (platform: string | undefined | null): string[] {
  if (!platform) return AR15_ZONE_ORDER
  const key = platform.toLowerCase()
  if (PLATFORM_ZONE_ORDER[key]) return PLATFORM_ZONE_ORDER[key]
  if (platformIsAkSchematic(platform)) return AK_ZONE_ORDER
  return AR15_ZONE_ORDER
}

/**
 * Platforms that use AK-specific zones and mount type selector.
 */
export const AK_PLATFORMS = ['ak', 'ak47', 'ak74', 'akm', 'rpk']

/**
 * True when the schematic picker would use the AR-15 SVG (must run before AK checks — same order as PlatformSchematic’s picker).
 */
export function platformIsArSchematic (platform: string | undefined | null): boolean {
  if (!platform) return false
  const p = platform.toLowerCase()
  return p.includes('ar') || p.includes('msr') || p.includes('m4') || p.includes('m16')
}

/**
 * True when the schematic picker would use the AK SVG / AKM TSX artwork (excludes strings that map to AR first).
 */
export function platformIsAkSchematic (platform: string | undefined | null): boolean {
  if (!platform) return false
  const p = platform.toLowerCase()
  if (platformIsArSchematic(platform)) return false
  return p.includes('ak') || p.includes('ak-47') || p.includes('ak-74') || p.includes('amd')
}

/**
 * Returns true if the platform is any AK variant (schematic + workbench AK behavior).
 */
export function isAkPlatform (platform: string | undefined | null): boolean {
  return platformIsAkSchematic(platform)
}

export function getZoneCategories (zone: string): string[] {
  return ZONE_CATEGORY_MAP[zone]?.categories ?? []
}

export function isMultiPartZone (zone: string): boolean {
  return ZONE_CATEGORY_MAP[zone]?.multiPart ?? false
}

/**
 * Maps a part category to its SVG zone name.
 * Sub-categories (e.g. "Magazine762") map to their parent zone ("Magazine")
 * so that zone highlights and overrides stay consistent with the schematic.
 */
export function getCategoryZone (category: string): string {
  const SUB_TO_ZONE: Record<string, string> = {
    Magazine762: 'Magazine',
    Magazine545: 'Magazine',
    MagazineDrum: 'Magazine'
  }
  return SUB_TO_ZONE[category] ?? category
}
